import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProviderSpot } from '../../../../../core/models/provider-parking/provider-spot.model';
import { ApiProviderSpotService } from '../../../../../core/services/api/provider-parking/api-provider-spot.service';
import { ApiTableService } from '../../../../../core/services/api-table.service';
import { ToastService } from 'angular-toastify';
import { optionResponse, dataDto } from '../../../../../core/models/form.model';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-modal-provider-spot',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-provider-spot.component.html',
  styleUrl: './modal-provider-spot.component.css'
})
export class ModalProviderSpotComponent implements OnInit {
  @Input() spotData: ProviderSpot | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  public form: ProviderSpot = this.getInitialForm();
  public isLoading: boolean = false;
  public openDropdown: string | null = null;

  // Dropdowns
  public siteList: optionResponse | null = null;
  public spotTypes = ['ทางเข้า', 'ทางออก', 'Webpay', 'Handheld', 'BackOffice', 'ตู้รับชำระ'];
  public vehicleTypes = ['รถยนต์', 'จักรยานยนต์', 'รถบรรทุก', 'อื่นๆ'];
  public rateOptions = ['00000 - Parking Rate', '10000 - Member Rate', '20000 - VIP Rate', '99999 - Other'];
  public areaOptions = ['อาคารจอด The Market 1', 'อาคารจอด The Market 2', 'อาคารจอด AroundD', 'ลานจอดหน้าอาคาร'];

  // Search states for dropdowns
  public searchSpotTypeText: string = '';
  public searchAreaText: string = '';
  public searchVehicleTypeText: string = '';
  public searchRateText: string = '';
  public searchSiteText: string = '';

  get filteredSpotTypes(): string[] {
    const s = (this.searchSpotTypeText || '').toLowerCase();
    return this.spotTypes.filter(t => t.toLowerCase().includes(s));
  }

  get filteredAreas(): string[] {
    const s = (this.searchAreaText || '').toLowerCase();
    return this.areaOptions.filter(a => a.toLowerCase().includes(s));
  }

  get filteredVehicleTypes(): string[] {
    const s = (this.searchVehicleTypeText || '').toLowerCase();
    return this.vehicleTypes.filter(v => v.toLowerCase().includes(s));
  }

  get filteredRates(): string[] {
    const s = (this.searchRateText || '').toLowerCase();
    return this.rateOptions.filter(r => r.toLowerCase().includes(s));
  }

  get filteredSites(): any[] {
    if (!this.siteList || !this.siteList.data) return [];
    const s = (this.searchSiteText || '').toLowerCase();
    return this.siteList.data.filter((site: any) => site.name.toLowerCase().includes(s));
  }

  constructor(
    private apiProviderSpotService: ApiProviderSpotService,
    private apiTableService: ApiTableService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    if (this.spotData) {
      this.form = { ...this.spotData };
      this.searchSpotTypeText = this.form.spotType || '';
      this.searchAreaText = this.form.area || '';
      this.searchVehicleTypeText = this.form.vehicleType || '';
      this.searchRateText = this.form.defaultRate || '';
      this.searchSiteText = this.form.site || '';
    }
    this.fetchSites();
  }

  public onClickInsideModal(event: Event): void {
    event.stopPropagation();
    this.closeDropdowns();
  }

  public closeDropdowns(): void {
    if (this.openDropdown === 'typeSelect') {
      if (this.spotTypes.includes(this.searchSpotTypeText)) {
        this.form.spotType = this.searchSpotTypeText;
      } else {
        this.searchSpotTypeText = this.form.spotType || '';
      }
    } else if (this.openDropdown === 'areaSelect') {
      if (this.areaOptions.includes(this.searchAreaText)) {
        this.form.area = this.searchAreaText;
      } else {
        this.searchAreaText = this.form.area || '';
      }
    } else if (this.openDropdown === 'vTypeSelect') {
      if (this.vehicleTypes.includes(this.searchVehicleTypeText)) {
        this.form.vehicleType = this.searchVehicleTypeText;
      } else {
        this.searchVehicleTypeText = this.form.vehicleType || '';
      }
    } else if (this.openDropdown === 'rateSelect') {
      if (this.rateOptions.includes(this.searchRateText)) {
        this.form.defaultRate = this.searchRateText;
      } else {
        this.searchRateText = this.form.defaultRate || '';
      }
    } else if (this.openDropdown === 'siteSelect') {
      const siteNames = this.siteList?.data?.map((s: any) => s.name) || [];
      if (siteNames.includes(this.searchSiteText)) {
        this.form.site = this.searchSiteText;
      } else {
        this.searchSiteText = this.form.site || '';
      }
    }
    this.openDropdown = null;
  }

  public onInputFocus(menu: string): void {
    console.log("onInputFocus", menu);
    if (this.openDropdown && this.openDropdown !== menu) {
      this.closeDropdowns();
    }
    this.openDropdown = menu;
  }

  private getInitialForm(): ProviderSpot {
    return {
      id: 0, name: '', description: '', posId: '', ip: '', spotType: '', area: '',
      vehicleType: '', apiUser: '', apiPass: '', connectDate: '', monitor: 'ปกติ,ปกติ',
      status: '', deviceKey: '', defaultRate: '00000', prefix: '', sapName: '',
      sapNameM: '', site: '', checkCode: ''
    };
  }

  private fetchSites(): void {
    this.apiTableService.getParkingSiteList().subscribe({
      next: (res) => this.siteList = res,
      error: (err) => console.error('Fetch sites error:', err)
    });
  }

  public toggleDropdown(id: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdown === id) {
      this.closeDropdowns();
    } else {
      if (this.openDropdown) this.closeDropdowns();
      this.openDropdown = id;
    }
  }

  public selectOption(field: keyof ProviderSpot, value: any): void {
    (this.form as any)[field] = value;
    if (field === 'spotType') this.searchSpotTypeText = value;
    else if (field === 'area') this.searchAreaText = value;
    else if (field === 'vehicleType') this.searchVehicleTypeText = value;
    else if (field === 'defaultRate') this.searchRateText = value;
    else if (field === 'site') this.searchSiteText = value;
    this.openDropdown = null;
  }

  public onSave(): void {
    this.closeDropdowns(); // Ensure inputs are validated first

    if (!this.form.name || !this.form.site) {
      this.toast.error('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง');
      return;
    }

    this.isLoading = true;
    this.apiProviderSpotService.createProviderSpot(this.form)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.toast.success('บันทึกข้อมูลจุดให้บริการสำเร็จ');
          this.saveSuccess.emit();
          this.close.emit();
        },
        error: (err) => {
          console.error('Save error:', err);
          this.toast.error('ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
        }
      });
  }

  public onCancel(): void {
    this.close.emit();
  }
}
